import { nanoid } from "nanoid/non-secure";
import { map } from "ramda";
import {
  type NotificationIntent,
  type NotificationOptions,
  type NotificationProps,
  notify as __notify,
  VStack,
} from "widgets";

import { type Notification as NotificationGql } from "@/graphql.ts";
import { ALERTS_QUEUE_CONTAINER_ID } from "@/settings/settings.ts";

import { ErrorNotificationChildren, type ErrorNotificationProps, FromNotification } from "./Notification.tsx";

export const notify = {
  info: (props: NotificationProps, options: NotificationOptions = {}) => {
    return __notify.info(props, { containerId: ALERTS_QUEUE_CONTAINER_ID, ...options });
  },
  success: (props: NotificationProps, options: NotificationOptions = {}) => {
    return __notify.success(props, { containerId: ALERTS_QUEUE_CONTAINER_ID, ...options });
  },
  warning: (props: NotificationProps, options: NotificationOptions = {}) => {
    return __notify.warning(props, { containerId: ALERTS_QUEUE_CONTAINER_ID, ...options });
  },
  error: (props: NotificationProps & ErrorNotificationProps, options: NotificationOptions = {}) => {
    return __notify.error(
      {
        ...props,
        children: (
          <ErrorNotificationChildren seeMoreDetailsLink={props.seeMoreDetailsLink}>
            {props.children}
          </ErrorNotificationChildren>
        ),
      },
      { containerId: ALERTS_QUEUE_CONTAINER_ID, ...options },
    );
  },
  /** To be used in zustand or any part of the application where JSX should be used */
  fromErrors: (props: Omit<NotificationProps, "children"> & { errors: Error[] }, options: NotificationOptions = {}) => {
    return __notify.error(
      {
        ...props,
        children: (
          <VStack>
            {map(
              (err) => (
                <div>{err.message}</div>
              ),
              props.errors,
            )}
          </VStack>
        ),
      },
      { containerId: ALERTS_QUEUE_CONTAINER_ID, ...options },
    );
  },
  fromNotification: (notification: NotificationGql, options: NotificationOptions = {}) => {
    const toastId = options.toastId ?? nanoid();

    import.meta.env.DEV || (import.meta.env.MODE === "staging" && console.warn("Load single fromNotifications"));

    return __notify.withJSX(
      <FromNotification
        notification={notification}
        toastId={toastId}
        markAsReadOverInteraction={options.markAsReadOverInteraction}
      />,
      toastId,
      { containerId: ALERTS_QUEUE_CONTAINER_ID, ...options },
    );
  },
  update: (
    currentToastId: string | number,
    props: NotificationProps,
    intent: NotificationIntent,
    options: NotificationOptions = {},
  ) => {
    return __notify.update(currentToastId, props, intent, { containerId: ALERTS_QUEUE_CONTAINER_ID, ...options });
  },
  clearWaitingQueue: __notify.clearWaitingQueue,
};
